import { z } from 'zod';
import { uuid } from './primitives';

/**
 * Operator admin contracts (Master Spec §14, #278). Cross-tenant, operator-only
 * surfaces that let a platform OPERATOR (the `requireOperator` env allow-list, NOT a
 * tenant role) see across workspaces. Currently: the workspace picker behind the
 * operator's cross-workspace agents/runs view.
 *
 * Privacy (I6): these expose operator-visible DISPLAY fields only — a workspace's id +
 * name and its owner's name + email. No secret, token, or credential ever crosses this
 * boundary; the cross-tenant SQL that backs it lives ONLY in the allow-listed `db/raw`
 * helper, behind `requireOperator` at the route.
 */

/** A workspace's owner, as shown in the operator workspace picker (display fields only). */
export const adminWorkspaceOwner = z.object({
  name: z.string(),
  email: z.string(),
});
export type AdminWorkspaceOwner = z.infer<typeof adminWorkspaceOwner>;

/** One workspace row in the operator cross-tenant workspace list. */
export const adminWorkspace = z.object({
  id: uuid,
  name: z.string(),
  owner: adminWorkspaceOwner,
});
export type AdminWorkspace = z.infer<typeof adminWorkspace>;

/**
 * The `GET /api/admin/workspaces` response — EVERY workspace across ALL tenants, for the
 * operator workspace picker. Operator-gated (`requireOperator`); a non-operator gets 403.
 */
export const adminWorkspaceList = z.array(adminWorkspace);
export type AdminWorkspaceList = z.infer<typeof adminWorkspaceList>;

// ── #322: Poller Activity lookups (operator-gated) ───────────────────────────
// Two cross-tenant reads behind the operator gate that power the Poller Activity page:
// an email typeahead over platform users, and the selected user's connections. Display
// fields only (I6) — id/email/name and provider/external account; no secret ever crosses.

/** Default / max page size for the operator user typeahead. */
export const ADMIN_USER_SEARCH_DEFAULT_LIMIT = 10;
export const ADMIN_USER_SEARCH_MAX_LIMIT = 25;

/** One platform-user row in the operator email typeahead (display fields only). */
export const adminUser = z.object({
  id: uuid,
  email: z.string(),
  name: z.string(),
});
export type AdminUser = z.infer<typeof adminUser>;

/**
 * `GET /api/admin/users/search?q=<substr>&limit=<n>`: platform users whose email or name
 * CONTAINS `q` (case-insensitive), most-relevant first. Operator-gated; a non-operator gets 403.
 */
export const adminUserSearchResponse = z.object({
  users: z.array(adminUser),
});
export type AdminUserSearchResponse = z.infer<typeof adminUserSearchResponse>;

/** One connection row in the operator connection dropdown (metadata only, I6). */
export const adminConnection = z.object({
  id: uuid,
  workspaceId: uuid,
  provider: z.string(),
  externalAccount: z.string(),
});
export type AdminConnection = z.infer<typeof adminConnection>;

/**
 * `GET /api/admin/connections?email=<email>`: the LIVE (non-revoked) connections belonging to
 * the reporting user's workspaces (resolved from their email). Operator-gated; non-operator → 403.
 */
export const adminConnectionsResponse = z.object({
  connections: z.array(adminConnection),
});
export type AdminConnectionsResponse = z.infer<typeof adminConnectionsResponse>;
